import React from 'react';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { PageHero } from '../components/PageHero';
import { Truck, Package, Phone, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PlanYourVisitCTA } from '../components/PlanYourVisitCTA';
import { PHONE, PHONE_TEL } from '../constants/business';
export function FirearmTransfers() {
  useDocumentMeta('Firearm Transfers | Kjergaard Sports', 'Inbound and outbound firearm transfers, NFA transfers, and shipping for non-Minnesota customers at Kjergaard Sports in Lake Benton, Minnesota.');
  return <div data-id="element-502">
      <PageHero title="Firearm Transfers" image="https://cdn.magicpatterns.com/uploads/h2HHsk76CJxRY2pmsyL8mj/firearms-wall.jpg" alt="Firearms display at Kjergaard Sports — in-store transfers handled daily" data-id="element-503" />

      <section className="py-20 bg-brand-offwhite" data-id="element-504">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-505">
          <div className="max-w-3xl mb-16" data-id="element-506">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-507">
              A High-Volume Transfer Operation
            </h2>
            <p className="text-lg text-brand-charcoal/80 leading-relaxed" data-id="element-508">
              Kjergaard Sports handles a large volume of firearm transfers, both
              inbound and outbound. We process transfers daily, so the
              paperwork, shipping logistics, and compliance steps are part of
              our routine — not a one-off favor.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-509">
            <div className="bg-brand-bone p-8 rounded-sm border border-brand-cream" data-id="element-510">
              <Package className="w-10 h-10 text-brand-hunter mb-5" data-id="element-511" />
              <h3 className="text-2xl font-serif text-brand-charcoal mb-4" data-id="element-512">
                Inbound Transfers
              </h3>
              <p className="text-brand-charcoal/80 mb-6 leading-relaxed" data-id="element-513">
                Bring your dealer's FFL information when contacting us so we can
                coordinate the shipment to our store.
              </p>
              <div className="bg-brand-offwhite border border-brand-cream p-5 rounded-sm" data-id="element-514">
                <p className="text-brand-charcoal font-semibold mb-2" data-id="element-515">
                  Inbound firearm transfer:{' '}
                  <span className="text-brand-hunter" data-id="element-516">$35</span>
                </p>
                <p className="text-brand-charcoal font-semibold" data-id="element-517">
                  Inbound NFA item transfer:{' '}
                  <span className="text-brand-hunter" data-id="element-518">$100</span>
                </p>
              </div>
            </div>

            <div className="bg-brand-bone p-8 rounded-sm border border-brand-cream" data-id="element-519">
              <Truck className="w-10 h-10 text-brand-hunter mb-5" data-id="element-520" />
              <h3 className="text-2xl font-serif text-brand-charcoal mb-4" data-id="element-521">
                Outbound Transfers
              </h3>
              <p className="text-brand-charcoal/80 mb-4 leading-relaxed" data-id="element-522">
                Kjergaard Sports ships firearms regularly. Outbound transfers
                are a normal part of our daily work — we'll handle the
                paperwork, packaging, and shipping coordination to your
                destination dealer.
              </p>
              <p className="text-brand-charcoal/75 text-sm leading-relaxed" data-id="element-523">
                Call before shipping so we can confirm pricing and timing.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-brand-bone" data-id="element-524">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-525">
          <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-526">
            Non-Minnesota Customers
          </h2>
          <p className="text-lg text-brand-charcoal/80 mb-10 leading-relaxed" data-id="element-527">
            Sitting near the South Dakota and Iowa borders, we regularly help
            non-Minnesota customers with compliant transfer and shipping
            logistics. If you're buying a firearm from us as an out-of-state
            customer, we can ship it to your home-state dealer for lawful
            transfer where applicable.
          </p>

          <div className="bg-brand-cream border-l-4 border-brand-leather p-8 rounded-r-sm" data-id="element-528">
            <h3 className="text-xl font-serif text-brand-charcoal mb-3" data-id="element-529">
              Handguns for Out-of-State Customers
            </h3>
            <p className="text-brand-charcoal/85 leading-relaxed" data-id="element-530">
              Federal law prohibits Kjergaard Sports from transferring a handgun
              directly to a non-Minnesota resident. That should not stop you
              from shopping with us — we can ship the handgun to a dealer in
              your home state for lawful transfer where applicable. Many of our
              out-of-state handgun customers handle the purchase exactly this
              way.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-brand-offwhite" data-id="element-531">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-532">
          <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-533">
            NFA Transfers
          </h2>
          <p className="text-lg text-brand-charcoal/80 mb-6 leading-relaxed" data-id="element-534">
            We also handle NFA item transfers. See our Suppressors & NFA Process
            page for the full walk-through, including in-store fingerprints and
            Form 4 support.
          </p>
          <Link to="/nfa-process" className="inline-flex items-center text-brand-hunter font-semibold hover:text-brand-wood transition-colors" data-id="element-535">
            View the NFA Process →
          </Link>
        </div>
      </section>

      <section className="py-20 bg-brand-cream" data-id="element-536">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-537">
          <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-538">
            Call Before Shipping
          </h2>
          <p className="text-lg text-brand-charcoal/80 mb-10 leading-relaxed" data-id="element-539">
            Every transfer is slightly different. Call before shipping so we can
            confirm pricing, paperwork, and timing.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8" data-id="element-540">
            <a href={PHONE_TEL} className="bg-brand-hunter hover:bg-brand-wood text-brand-offwhite px-7 py-3.5 rounded-sm font-medium transition-colors inline-flex items-center justify-center" data-id="element-541">
              <Phone className="w-5 h-5 mr-2" data-id="element-542" />
              Call {PHONE}
            </a>
            <Link to="/contact" className="border-2 border-brand-hunter text-brand-hunter hover:bg-brand-hunter hover:text-brand-offwhite px-7 py-3.5 rounded-sm font-medium transition-colors inline-flex items-center justify-center" data-id="element-543">
              Ask About a Transfer
            </Link>
            <Link to="/contact" className="border border-brand-charcoal/20 hover:border-brand-leather text-brand-charcoal px-7 py-3.5 rounded-sm font-medium transition-colors inline-flex items-center justify-center" data-id="element-544">
              Plan Your Visit
            </Link>
          </div>

          <div className="bg-brand-offwhite border-l-4 border-brand-leather p-6 text-left max-w-2xl mx-auto" data-id="element-545">
            <div className="flex items-start" data-id="element-546">
              <Info className="w-5 h-5 text-brand-leather mr-3 flex-shrink-0 mt-0.5" data-id="element-547" />
              <p className="text-brand-charcoal/85 text-sm leading-relaxed" data-id="element-548">
                All transfers are handled in accordance with applicable federal,
                state, and local laws. Requirements vary. This page is general
                information only and is not legal advice. Please call the store
                with questions.
              </p>
            </div>
          </div>
        </div>
      </section>

      <PlanYourVisitCTA data-id="element-549" />
    </div>;
}